import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import Config from '@glass-designer/config'
import BaseTool from '@glass-designer/tools/baseTool'
import EditingCutout from '@glass-designer/objects/editingCutout'
import RectangleHole from '@glass-designer/model/holes/rectangleHole'
import CircleHole from '@glass-designer/model/holes/circleHole'
import OvalHole from '@glass-designer/model/holes/ovalHole'
import CustomHole from '@glass-designer/model/holes/customHole'
import LineObject from '@glass-designer/objects/lineObject'
import DatumLink from '@glass-designer/model/datumLink'

import GlassDesignerTypes from '@glass-designer/types'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'


/**
 * Add Center Cutout tool
 */
export default class AddCenterCutoutToShape extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.addCenterCutout
    this.draggingCutoutActive = false
    this.holeInfoList = []
    this.libraryItemInfo = null

    this.datumLinks = []
    this.cutoutBounds = null

    this.cutoutObject = new EditingCutout(editor)
    this.cutoutObject.setVisible(false)
    this.addChildObject(this.cutoutObject)

    this.snapLineDisplay = new LineObject(this.editor, {
      colour: '#FF00FF',
      opacity: 1.0
    })
    this.snapLineDisplay.setVisible(false)
    this.addChildObject(this.snapLineDisplay)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive() {
    super.onToolActive()

    // Reset
    this.libraryItemInfo = null
    this.draggingCutoutActive = false
    this.holeInfoList = []
    this.cutoutObject.setVisible(false)
    this.snapLineDisplay.setVisible(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.holeInfoList.forEach(holeInfo => {
      this.editor.removeSceneObject(holeInfo.holeObject)
      holeInfo.holeObject.dispose()
    })
    this.holeInfoList = []

    this.draggingCutoutActive = false
    this.cutoutObject.setVisible(false)
    this.snapLineDisplay.setVisible(false)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.draggingCutoutActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {

        this.cutoutObject.setPosition(rayInt)
      }

      let shape = this.editor.findClosestGlassShape(this.cutoutObject.position, { ignoreStockLinked: true })
      if(shape) {

        // If snap enabled, snap to nearest horizontal/vertical edge of another center cutout
        if(this.editor.getOptionSnapEnabled()) {
          let position = this._snapToCenterCutouts(shape, this.cutoutObject.position.clone())
          this.cutoutObject.setPosition(position)
        }

        // Calculate the 2 datum reference objects in the shape
        // Either segments or other holes
        this._calculateCutoutDatumLinks(shape, this.cutoutObject.position)

        this.cutoutObject.setDatumLinks(this.datumLinks)

        // Update the dimensions for the cutout to the shape
        this.editor.onToolEditingUpdated(shape, [this.cutoutObject])

        this.editor.flagRenderRequired()
      }
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(this.draggingCutoutActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {
        this.cutoutObject.setPosition(rayInt)
      }

      let shape = this.editor.findClosestGlassShape(this.cutoutObject.position, { ignoreStockLinked: true })
      if(shape) {
        // If snap enabled, snap to nearest horizontal/vertical edge of another center cutout
        if(this.editor.getOptionSnapEnabled()) {
          let position = this._snapToCenterCutouts(shape, this.cutoutObject.position.clone())
          this.cutoutObject.setPosition(position)
        }

        // Calculate the 2 datum reference objects in the shape
        // Either segments or other holes
        this._calculateCutoutDatumLinks(shape, this.cutoutObject.position)

        this._placeCutout(shape)

        this.editor.flagRenderRequired()
      }
    }
  }


  /**
   * A library item was dragged into the editor while this tool was active
   */
  libraryItemDraggedToEditor(itemInfo) {
    this.draggingCutoutActive = true

    this.datumLinks = []
    this.libraryItemInfo = itemInfo
    let itemData = itemInfo.itemData || {}

    this.holeObjectList = []

    this.cutoutObject.clear()

    // Support list of hole objects
    itemData.cutoutList.forEach(cutoutInfo => {
      var cutoutData = cutoutInfo.cutoutData
      if(!cutoutData)
        return

      var holeObject = null
      
      // Rectangle / Circle
      if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
         holeObject = new RectangleHole(this.editor, {
          width: parseInt(cutoutData.width || 100),
          height: parseInt(cutoutData.height || 50),
          cornerRadiusTL: parseInt(cutoutData.cornerRadiusTL || 5),
          cornerRadiusTR: parseInt(cutoutData.cornerRadiusTR || 5),
          cornerRadiusBR: parseInt(cutoutData.cornerRadiusBR || 5),
          cornerRadiusBL: parseInt(cutoutData.cornerRadiusBL || 5),
          angle: parseInt(cutoutData.angle || 0),
          isCutoutPolished: !!cutoutData.isCutoutPolished
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
        holeObject = new CircleHole(this.editor, {
          radius: parseFloat(cutoutData.radius || 5),
          isCutoutPolished: !!cutoutData.isCutoutPolished,
          isCountersunk: !!cutoutData.isCountersunk,
          countersunkRadius: parseFloat(cutoutData.countersunkRadius || 0)
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.oval) {
        holeObject = new OvalHole(this.editor, {
          width: parseInt(cutoutData.width || 5),
          height: parseInt(cutoutData.height || 5),
          isCutoutPolished: !!cutoutData.isCutoutPolished
        })
      } else if(cutoutData.holeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
        holeObject = new CustomHole(this.editor, {
          isCutoutPolished: !!cutoutData.isCutoutPolished,
          angle: parseInt(cutoutData.angle || 0),
          isOpenShape: false,
          isCornerShape: false,
          segmentList: cutoutData.segmentList
        })

      } else {
        // Unknown cutout type
        return
      }

      if(holeObject) {
        holeObject.setVisible(false)
        holeObject.setAnchorInfo(GlassDesignerTypes.HoleMeasurePointTypes.center, GlassDesignerTypes.HoleMeasurePointTypes.middle)

        var offset = new THREE.Vector2(0, 0)
        if(cutoutInfo.offset)
          offset.set(parseFloat(cutoutInfo.offset.x || 0), parseFloat(cutoutInfo.offset.y || 0))

        this.holeInfoList.push({
          holeObject: holeObject,
          offset: offset
        })

        this.cutoutObject.addHoleWithOffset(holeObject, offset)
      }
    })

    this.cutoutBounds = this.cutoutObject.calculateBounds()
    this.cutoutObject.setVisible(true)

    this.editor.flagRenderRequired()
  }


  /**
   * Release the cutout & place a new cutout onto the shape
   * Selects the cutout for editing mode
   */
  _placeCutout(shape) {
    this.draggingCutoutActive = false
    this.cutoutObject.setVisible(false)

    try {
      this.editor.startUndoGroup('Adding center cutout')
      this.editor.addUndoShape(shape)

      this.holeInfoList.forEach(holeInfo => {

        // Calculate datum links for each hole with offset adjusted
        var holeDatumLinks = [
          new DatumLink(),
          new DatumLink()
        ]

        let holePosition = this.cutoutObject.position.clone().add(holeInfo.offset)
        EditorDatumUtils.calculateClosestDatumLinks(shape, holeDatumLinks, holePosition, { ignoreAllHoles: false })

        // Run checks here. If outside shape, flag error
        if(!shape.isPointInsideShape(holePosition))
          throw new EditorUserError('Center cutout is outside of glass panel')

        // Apply guidelines for standard items (ie, not custom library items)
        if(this.libraryItemInfo && this.libraryItemInfo.isCoreItem)
          this._applyCenterCutoutGuidelines(shape, holeInfo.holeObject)

        // Datum links to segments / other holes
        holeInfo.holeObject.setDatumLinks(holeDatumLinks)
        holeInfo.holeObject.setVisible(true)
        shape.addCenterCutout(holeInfo.holeObject)

        // Clear hole object from list
        // Now owned by the shape
        holeInfo.holeObject = null
      })

      this.editor.closeCurrentUndoGroup()

      // Clear hole info list
      // Don't dispose, shape owns the hole now
      this.holeInfoList = []

    } catch(err) {
      this.editor.cancelCurrentUndoGroup()

      // Dispose any hole objects not placed
      this.holeInfoList.forEach(holeInfo => {
        if(holeInfo.holeObject)
          holeInfo.holeObject.dispose()
      })

      // Display error (eg, hole outside of shape or whatever)
      if(err instanceof EditorUserError)
        this.editor.component.errorMessage('Error placing cutout', err.message)

      console.error(err)
    }

    // Reset dimensions for editing
    this.editor.onShapeUpdated(shape)
    this.editor.dimensionHelper.resetEditingDimensions()

    this.holeInfoList = []

    this.closeTool()
  }


  /**
   * Apply the minimum corner radius info based on guidelines for the selected shape to a standard rectangle/circle cutout
   * Not done to custom library items as they are to spec. They'll just show warnings if invalid
   */
  _applyCenterCutoutGuidelines(shape, holeObject) {
  
    if(holeObject.holeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      let minRadius = this.editor.getGuidelineHelper().calculateMinimumCornerRadiusForThickness(shape.getGlassType(), shape.getThickness(), 25)    // 25% for 1 corner out of 4

      let cornerRadius = Math.max(holeObject.getCornerRadius(), minRadius)
      holeObject.setCornerRadius(cornerRadius)
      holeObject.holeSizeUpdated()
    }
    if(holeObject.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      let minRadius = this.editor.getGuidelineHelper().calculateMinimumCornerRadiusForThickness(shape.getGlassType(), shape.getThickness(), 100)    // 100%, full circle

      let radius = Math.max(holeObject.radius, minRadius)
      holeObject.radius = radius
      holeObject.holeSizeUpdated()
    }
  }


  /**
   * Calculate the best datum links for an object at its current position relative to the selected shape
   * Updates datumLinks array
   * Attempts to find 2 best datum links
   */
  _calculateCutoutDatumLinks(shape, position) {
    if(this.datumLinks.length == 0) {
      this.datumLinks.push(new DatumLink())
      this.datumLinks.push(new DatumLink())
    }

    EditorDatumUtils.calculateClosestDatumLinks(shape, this.datumLinks, position, { ignoreAllHoles: false })
  }


  /**
   * Snap to other center cutouts in the same shape
   */
  _snapToCenterCutouts(shape, position) {
    var closestCutoutEdge = {}

    var displaySnap = false
    shape.getCenterCutoutList().forEach(cutout => {
      let otherBounds = cutout.calculateSegmentBounds()

      // If inside the other cutout, don't snap
      if(otherBounds.containsPoint(position))
        return

      let otherCenterX = (otherBounds.min.x + otherBounds.max.x) / 2
      let otherCenterY = (otherBounds.min.y + otherBounds.max.y) / 2

      let leftEdgeDist = Math.abs(position.x + this.cutoutBounds.min.x - otherBounds.min.x)
      let centerEdgeDist = Math.abs(position.x - otherCenterX)
      let rightEdgeDist = Math.abs(position.x + this.cutoutBounds.max.x - otherBounds.max.x)

      let topEdgeDist = Math.abs(position.y + this.cutoutBounds.min.y - otherBounds.min.y)
      let middleEdgeDist = Math.abs(position.y - otherCenterY)
      let bottomEdgeDist = Math.abs(position.y + this.cutoutBounds.max.y - otherBounds.max.y)

      this._addSnapEdge(closestCutoutEdge, leftEdgeDist, this.cutoutBounds.min.x, 0, otherBounds.min.x, otherCenterY, false)
      this._addSnapEdge(closestCutoutEdge, centerEdgeDist, 0, 0, otherCenterX, otherCenterY, false)
      this._addSnapEdge(closestCutoutEdge, rightEdgeDist, this.cutoutBounds.max.x, 0, otherBounds.max.x, otherCenterY, false)
      this._addSnapEdge(closestCutoutEdge, topEdgeDist, 0, this.cutoutBounds.min.y, otherCenterX, otherBounds.min.y, true)
      this._addSnapEdge(closestCutoutEdge, middleEdgeDist, 0, 0, otherCenterX, otherCenterY, true)
      this._addSnapEdge(closestCutoutEdge, bottomEdgeDist, 0, this.cutoutBounds.max.y, otherCenterX, otherBounds.max.y, true)
    })

    if(closestCutoutEdge.hasEdge) {
      displaySnap = true

      // Snap position
      if(closestCutoutEdge.isVertical)
        position.y = closestCutoutEdge.positionY - closestCutoutEdge.offsetY
      else
        position.x = closestCutoutEdge.positionX - closestCutoutEdge.offsetX
      
      // Setup snap line display
      if(closestCutoutEdge.isVertical)
        this.snapLineDisplay.setStartPosition(new THREE.Vector2(position.x - closestCutoutEdge.offsetX, closestCutoutEdge.positionY))
      else
        this.snapLineDisplay.setStartPosition(new THREE.Vector2(closestCutoutEdge.positionX, position.y - closestCutoutEdge.offsetY))
      this.snapLineDisplay.setEndPosition(new THREE.Vector2(closestCutoutEdge.positionX, closestCutoutEdge.positionY))
    }

    this.snapLineDisplay.setVisible(displaySnap)
    
    return position
  }


  /**
   * Add a snapped edge candidate to a snap object
   */
  _addSnapEdge(closestEdgeInfo, dist, offsetX, offsetY, positionX, positionY, isVertical) {
    if(dist > Config.Settings.centerCutoutMaxSnapDistance)
      return

    if(dist < closestEdgeInfo.distance || !closestEdgeInfo.hasEdge) {
      closestEdgeInfo.hasEdge = true
      closestEdgeInfo.isVertical = isVertical
      closestEdgeInfo.distance = dist
      closestEdgeInfo.offsetX = offsetX
      closestEdgeInfo.offsetY = offsetY
      closestEdgeInfo.positionX = positionX
      closestEdgeInfo.positionY = positionY
    }    
  }
}